import { FC, useEffect, useState, type JSX } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Layout, NextButton, StepHeader } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, InputUsage, RadioGroup, RadioTile, Stack, Text, TextLink } from '@eneco/sparky';
import { ElectricalOutlet, SolarPanel } from '@eneco/sparky/illustrations';
import { usePlaceholderContent } from '@sitecore/common';
import { SmeSolarPanelsStepRendering } from '@sitecore/types/manual/smeFlowExperiment/SMESolarPanelsChoiceStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { SolarPanelsOutputCalculator } from '../../components';
import { FlowContextSme } from '../../context';

interface FormFields {
  hasSolarPanels: string;
  solarPanelsOutput: number;
}

const generateInputElement = (value: string, label: string): JSX.Element => {
  let icon: JSX.Element;

  if (value === 'hasPanels') {
    icon = <SolarPanel size="small" />;
  } else {
    icon = <ElectricalOutlet size="small" />;
  }
  return (
    <RadioTile key={value} value={value} icon={icon}>
      {label}
    </RadioTile>
  );
};

export const SolarPanelsStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const hasSolarPanels = useFlowSelector(state => state.context.hasSolarPanels);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { SmeSolarPanelsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SmeSolarPanelsStep: SmeSolarPanelsStepRendering;
  }>();

  const formSchema = yup.object({
    hasSolarPanels: yup.string().required(textualData?.choice?.hasSolarPanelsRadio?.value?.requiredMessage),
    solarPanelsOutput: yup
      .number()
      .integer(textualData?.redelivery?.solarPanelsOutputFormField?.value?.validationMessage)
      .typeError(textualData?.redelivery?.solarPanelsOutputFormField?.value?.validationMessage ?? '')
      .required(textualData?.redelivery?.solarPanelsOutputFormField?.value?.requiredMessage)
      .min(1, textualData?.redelivery?.solarPanelsOutputFormField?.value?.validationMinValueMessage)
      .max(99999, textualData?.redelivery?.solarPanelsOutputFormField?.value?.validationMaxValueMessage),
  });

  const toSolarPanelsValue = (hasPanels?: boolean) => {
    if (hasPanels === undefined) return undefined;

    return hasPanels ? 'hasPanels' : 'noPanels';
  };

  const {
    handleSubmit,
    control,
    getValues,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      hasSolarPanels: toSolarPanelsValue(hasSolarPanels),
      solarPanelsOutput: 1,
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const { hasSolarPanels: watchHasSolarPanels } = watch();

  useEffect(() => {
    if (watchHasSolarPanels === 'hasPanels') {
      setValue('solarPanelsOutput', 0);
    } else {
      setValue('solarPanelsOutput', 1);
    }
  }, [setValue, watchHasSolarPanels]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();
      const hasSolarPanels = formData.hasSolarPanels === 'hasPanels';
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          hasSolarPanels,
          solarPanelsOutput: hasSolarPanels ? formData.solarPanelsOutput : 0,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />

      <Controller
        control={control}
        name="hasSolarPanels"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            aria-labelledby="hasSolarPanels"
            name={name}
            direction="column"
            value={value}
            onValueChange={onChange}
            error={errors['hasSolarPanels']?.message}>
            {textualData?.choice?.hasSolarPanelsRadio?.value?.options?.map(option =>
              generateInputElement(option.name, option.label),
            )}
          </RadioGroup>
        )}
      />

      {watchHasSolarPanels === 'hasPanels' && (
        <Box>
          <Stack gap={6}>
            <Stack gap={4}>
              <Text size="BodyM" weight="bold">
                {textualData?.redelivery?.title?.value}
              </Text>
              <Text>{textualData?.redelivery?.description.value}</Text>
            </Stack>
            <Box paddingBottom={6}>
              <TrackedDialog
                isOpen={isModalOpen}
                setOpen={setIsModalOpen}
                trigger={
                  <TextLink onClick={() => setIsModalOpen(true)} emphasis="high">
                    {textualData?.redeliveryEstimation?.solarPanelsRedeliveryEstimationDialog?.value?.triggerText}
                  </TextLink>
                }
                title={textualData?.redeliveryEstimation?.solarPanelsRedeliveryEstimationDialog?.value?.title}>
                <RichText
                  html={textualData?.redeliveryEstimation?.solarPanelsRedeliveryEstimationDialog?.value?.content}
                />
                <Box paddingTop="4">
                  <SolarPanelsOutputCalculator
                    shouldSubmit={false}
                    textualData={textualData}
                    handleAction={(output: number) => {
                      setValue('solarPanelsOutput', output);
                      trigger('solarPanelsOutput');
                      setIsModalOpen(false);
                    }}
                    handleCancel={() => {
                      setIsModalOpen(false);
                    }}
                  />
                </Box>
              </TrackedDialog>
            </Box>
          </Stack>

          <Stack>
            <Controller
              control={control}
              name="solarPanelsOutput"
              render={({ field }) => (
                <InputUsage
                  label={textualData?.redelivery?.solarPanelsOutputFormField?.value?.label ?? ''}
                  placeholder={textualData?.redelivery?.solarPanelsOutputFormField?.value?.placeholder}
                  hint={textualData?.redelivery?.solarPanelsOutputFormField?.value?.hint}
                  error={errors.solarPanelsOutput?.message}
                  type="redelivery"
                  unit="kWh"
                  {...field}
                />
              )}
            />
          </Stack>
        </Box>
      )}

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
